import React, { useState } from 'react'
import components from '../Table'
import Checkbox from '../Checkbox'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import classNames from 'classnames'
import { Menu, Transition } from '@headlessui/react'
import DropdownMenu from '../DropdownMenu'
import Icon from '../Icon'
import Button from '../Button'
import MenuButton from '../MenuButton'

const DataTable = ({
  components: overwrittenComponents = {},
  head = {},
  body = {},
  entries = [],
  indicators = [],
  indicatorsClassName = '',
  messages = {},
  noDataMessage = '',
  setSorting,
  sorting = { key: null, direction: null },
  rowSelectionKey = "uid",
  onRowSelected = null,
  rowsSelected = [],
  rowActions = [],
  state,
  className = '',
  ...props
}) => {
  const uniqueId = Object.keys(body).join('|')

  const semanticComponents = {
    ...Object.entries(components).reduce((memo, [key, Component]) => ({
      ...memo, [key]: ({ state, highlighted, childProps, ...props }) => <Component {...props } />
    }), {}),
    ...overwrittenComponents,
  }

  const headThs = Object.keys(body).reduce((memo, key) => ({
    ...memo, [key]: head[key] || {},
  }), {})

  return (
    <semanticComponents.Table className={className}>
      <semanticComponents.Thead>
        <semanticComponents.Tr state={state}>
          { (!!indicators.length || onRowSelected) && 
            <semanticComponents.Th className="w-24 space-x-1 xl:space-x-1.5">
              { onRowSelected &&
                <Checkbox
                  onChange={() => (entries || []).map((entry) => entry[rowSelectionKey]).map(onRowSelected)}
                  checked={(entries || []).map((entry) => entry[rowSelectionKey]).every((id) => rowsSelected.includes(id))}
                />
              }
            </semanticComponents.Th>
          }

          { Object.entries(headThs).map(([key, options]) => {
            let props = {
              highlighted: sorting.key === key,
              onClick: () => setSorting && setSorting((prev) => ({
                key,
                direction: prev.key !== key
                  ? prev.direction
                  : prev.direction === 'DESC' ? 'ASC' : 'DESC'
              })),
              children: key in messages ? (
                <span>{ messages[key] }</span>
              ) : (
                <span>{ key }</span>
              ),
            }

            const output = options instanceof Function ? options(props, state) : options

            if (React.isValidElement(output)) {
              props.children = output
            } else {
              props = { ...props, ...output }
            }

            const { sortable = false, label, onClick, title, className, children, ...rest } = props

            return (
              <semanticComponents.Th
                key={key}
                onClick={sortable ? onClick : undefined}
                className={classNames(className, 'group', {
                  'cursor-pointer': sortable,
                })}
                title={title}
                {...rest}
              >
                { label ? (
                  <span>{ label }</span>
                ) : children }

                { sorting?.key && sorting?.direction &&
                  <FontAwesomeIcon
                    className={classNames('ml-1', {
                      'hidden': !sortable,
                      'invisible': sortable && key != sorting.key,
                      'group-hover:visible': sortable,
                    })}
                    icon={{
                      ASC: 'fa-solid fa-arrow-up',
                      DESC: 'fa-solid fa-arrow-down'
                    }[sorting.direction ?? 'DESC']}
                  />
                }
              </semanticComponents.Th>
            )
          }) }

          { !!rowActions.length && 
            <semanticComponents.Th className="text-right">
              <Icon icon={['fal', 'ellipsis-vertical']} />
            </semanticComponents.Th>
          }
        </semanticComponents.Tr>
      </semanticComponents.Thead>

      <semanticComponents.Tbody>
        { entries && entries.map((entry, i) => {
          if (entry._groupHeader) {
            const colCount =
              Object.keys(body).length +
              (indicators.length || onRowSelected ? 1 : 0) +
              (rowActions.length ? 1 : 0)
            const groupIds = entry._groupIds || []
            const hasGroupCheckbox = !!onRowSelected && groupIds.length > 0
            const allGroupSelected = hasGroupCheckbox && groupIds.every(id => rowsSelected.includes(id))
            return (
              <semanticComponents.Tr key={`${uniqueId}-group-${i}`}>
                {hasGroupCheckbox && (
                  <semanticComponents.Td className="bg-gray-50 border-t border-gray-100 w-24">
                    <Checkbox
                      checked={allGroupSelected}
                      onChange={() => {
                        groupIds.forEach(id => {
                          const isSelected = rowsSelected.includes(id)
                          if (allGroupSelected ? isSelected : !isSelected) onRowSelected(id)
                        })
                      }}
                    />
                  </semanticComponents.Td>
                )}
                <semanticComponents.Td colSpan={hasGroupCheckbox ? colCount - 1 : colCount} className="bg-gray-50 border-t border-gray-100">
                  <span className="text-xs font-medium text-gray-400 uppercase tracking-wide">
                    {entry._label}
                  </span>
                </semanticComponents.Td>
              </semanticComponents.Tr>
            )
          }

          return (
            <semanticComponents.Tr
              key={`${uniqueId}-entry-${i}`}
              childProps={entry}
              state={state}
            >
              { (!!indicators.length || onRowSelected) &&
                <semanticComponents.Td childProps={entry} state={state}>
                  <span className={classNames('flex items-center justify-between space-x-1 xl:space-x-1.5', indicatorsClassName)}>
                    { onRowSelected &&
                      <Checkbox
                        onChange={() => onRowSelected(entry[rowSelectionKey])}
                        checked={rowsSelected.includes(entry[rowSelectionKey])}
                      />
                    }

                    { indicators.map((CustomComponent, i) => (
                      <CustomComponent key={`${uniqueId}-entry-indicator-${i}`} {...entry} />
                    )) }
                  </span>
                </semanticComponents.Td>
              }

              { Object.entries(body).map(([key, options]) => {
                let props = {
                  children: entry[key],
                }

                const output = options instanceof Function ? options({ ...props, ...entry }, state) : options

                if (React.isValidElement(output)) {
                  props.children = output
                } else {
                  props = { ...props, ...output }
                }

                const { children, ...rest } = props

                return (
                  <semanticComponents.Td
                    key={`${uniqueId}-entry-col-${key}`}
                    childProps={entry}
                    state={state}
                    {...rest}
                  >
                    { children }
                  </semanticComponents.Td>
                )
              }) }

              { !!rowActions.length &&
                <semanticComponents.Td onClick={(e) => e.stopPropagation()}>
                  <DropdownMenu
                    className="relative ml-3"
                    button={{
                      className: 'px-2',
                      variant: 'inline',
                      size: 'sm',
                      icon: ['fal', 'ellipsis-vertical'],
                    }}
                    items={rowActions.map(({ label, onClick, isVisible, ...rest }) => ({
                      children: label,
                      onClick: () => onClick ? onClick(entry) : undefined,
                      isVisible: () => isVisible ? isVisible(entry) : true,
                      ...rest,
                    })).filter(({isVisible}) => isVisible())}
                  />
                </semanticComponents.Td>
              }
            </semanticComponents.Tr>
          )
        }) }
        {entries && !entries.length && noDataMessage && noDataMessage !== '' &&
          <semanticComponents.Tr>
            <semanticComponents.Td colSpan={Object.entries(headThs).length + 1}>
              <p className='font-medium font-base text-center'>{noDataMessage}</p>
            </semanticComponents.Td>
          </semanticComponents.Tr>
        }
      </semanticComponents.Tbody>
    </semanticComponents.Table>
  )
}

export default DataTable
